import React from 'react';
import { useRef, useState, useEffect } from 'react'; 
import { format, parse } from 'date-fns';
import { useNavigate, useLocation } from 'react-router-dom';

import { Card, CardContent, Typography, Button, Box } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'; 
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import RatingStars from './RatingStars';

const CoachCard = ({ coach, product, role, company }) => {

    const navigate = useNavigate(); 
    const location = useLocation();
    // const { targetRole, targetCompany } = location.state || {};
    
    const scrollRef = useRef(null);
    const [isScrollable, setIsScrollable] = useState(false);
    const [isAtBottom, setIsAtBottom] = useState(false);

    useEffect(() => {
        const el = scrollRef.current;
      
        if (!el) return;
      
        // 1. Set isScrollable
        setIsScrollable(el.scrollHeight > el.clientHeight);
      
        // 2. Scroll handler to update isAtBottom
        const checkScroll = () => {
          const reachedBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
          setIsAtBottom(reachedBottom);
        };
      
        // Initial check on mount
        checkScroll();
      
        // Listen for scroll
        el.addEventListener('scroll', checkScroll);
      
        return () => {
          el.removeEventListener('scroll', checkScroll);
        };
      }, [coach]);

    // Function to format the date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
    
        try {
            const date = new Date(dateString);
            // Ensure the date object is valid before formatting
            if(isNaN(date.getTime())) {
                throw new Error('NA');
            }
            const formattedDate = format(date, 'MMM do, EE'); 
            return formattedDate;
        } catch (error) {
            return 'NA'; 
        }
    };

    const getCoachedRange = (mockCount, sessionCount) => {
        const total = (mockCount || 0) + (sessionCount || 0);
        if (total === 0) return null;
        if (total < 10) return "1–10";
        if (total < 20) return "10+";
        if (total < 50) return "20+";
        if (total < 100) return "50+";
        if (total < 200) return "100+";
        if (total < 500) return "200+";
        return "500+";
    };

    const coachedRange = getCoachedRange(coach.mock_interviews_conducted, coach.sessions_completed);

    const getInterviewedRange = (interviewedList) => {
        if (!Array.isArray(interviewedList)) return null;
      
        const total = interviewedList
          .filter(item => item.count !== null && item.count > 0)
          .reduce((sum, item) => sum + item.count, 0);
      
        if (total === 0) return null;
        if (total < 10) return "1–10";
        if (total < 20) return "10+";
        if (total < 50) return "20+";
        if (total < 100) return "50+";
        if (total < 200) return "100+";
        if (total < 500) return "200+";
        return "500+";
    };

    const interviewedRange = getInterviewedRange(coach.candidates_interviewed);

    return (
        <Box 
            sx={{ 
                position: 'relative',
            }}
        >
            {/* NEW Ribbon */}
            {coach.sessions_completed < 5 && (
                <Box
                    sx={{
                        mr: {xs: 29, sm: 31, md: 34},
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        width: '45px',
                        height: '50px',
                        backgroundImage: 'linear-gradient(180deg, #ff870f 0%, #ffd102 100%)', 
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        paddingBottom: '5px',
                        justifyContent: 'center',
                        borderBottomLeftRadius: '8px',
                        clipPath: 'polygon(0 0, 100% 0, 100% 80%, 50% 60%, 0 80%)',
                        zIndex: 1,
                    }}
                >
                    <Box sx={{ transform: 'translateY(-6px)' }}>
                        NEW
                    </Box>
                </Box>
            )}
            <Card sx={{
                width: { xs: 280, sm: 300, md: 320 },
                height: { xs: 420, sm: 400, md: 460 },
                m: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                borderRadius: 4,
            }}>
                <CardContent sx={{ 
                    flexGrow: 1,  
                    display: 'flex',  // Ensures correct layout
                    flexDirection: 'column',
                    minHeight: 0  // Critical: Prevents pushing button down
                }}>
                    {/* Central align only specific fields */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        textAlign: 'center',
                    }}>
                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                            {coach.unique_username}
                        </Typography>
                        <Typography gutterBottom variant="h6" component="div" sx={{ fontWeight: 'bold', color: '#424242', mt: 1 }}>
                            {coach.present_role} at {coach.present_company}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {coach.sessions_completed} {coach.sessions_completed === 1 ? 'session' : 'sessions'} completed
                        </Typography>
                        {/* Conditionally show offer info; There needs to be an API call to fetch this, currently manual on frontend */}
                        {coach.unique_username === '@udWHWP' && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Candidates landed Netflix & DoorDash offers
                        </Typography>
                        )}
                        {coach.unique_username === '@5jmIqG' && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Candidates landed DoorDash offers
                        </Typography>
                        )}
                        {coach.unique_username === '@qmZP1U' && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Candidates landed DoorDash offers
                        </Typography>
                        )}
                    </Box>
                    {/* DYNAMIC SCROLL AREA: Only Scrolls if Needed */}
                    <Box 
                        ref={scrollRef}
                        sx={{ 
                            flexGrow: 1, 
                            minHeight: 0,
                            overflowY: 'auto',  
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#616161 transparent', // makes scroll background transparent; remove for grey background
                            "&::-webkit-scrollbar": {
                                width: 0,
                            },
                        }}
                    >
                        {/* Align labels and values along the same x-axis */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', my: 0.4, alignItems: 'center' }}>
                                <Typography component="span" sx={{ width: '40%', fontWeight: 'bold' }}>Rating:</Typography>
                                <Box component="span" sx={{ 
                                    width: '60%', 
                                    textAlign: 'left', 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    alignItems: 'flex-start' 
                                }}>
                                {coach.rating !== null ? (
                                    <>
                                    <RatingStars rating={coach.rating} />
                                    {coach.rating_count > 0 && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                                            Based on {coach.rating_count} rating{coach.rating_count === 1 ? '' : 's'}
                                        </Typography>
                                    )}
                                    </>
                                ) : (
                                    <Typography color="text.secondary">NA</Typography>
                                )}
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', my: 0.4 }}>
                                <Typography component="span" sx={{ width: '40%', fontWeight: 'bold' }}>Experience:</Typography>
                                <Typography component="span" sx={{ width: '60%', textAlign: 'left', whiteSpace: 'pre-line' }}>
                                    {coach.past_companies.join('\n')}
                                </Typography>
                            </Box>
                            {coach.present_level && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', my: 0.4 }}>
                                    <Typography component="span" sx={{ width: '40%', fontWeight: 'bold' }}>Level:</Typography>
                                    <Typography component="span" sx={{ width: '60%', textAlign: 'left' }}>{coach.present_level}</Typography>
                                </Box>
                            )}
                            {coach.yoe > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', my: 0.4 }}>
                                    <Typography component="span" sx={{ width: '40%', fontWeight: 'bold' }}>YoE:</Typography>
                                    <Typography component="span" sx={{ width: '60%', textAlign: 'left' }}>{coach.yoe}</Typography>
                                </Box>
                            )}
                            {interviewedRange && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', my: 0.4 }}>
                                    <Typography component="span" sx={{ width: '40%', fontWeight: 'bold' }}>Interviewed:</Typography>
                                    <Typography component="span" sx={{ width: '60%', textAlign: 'left', display: 'flex', alignItems: 'center' }}>
                                        {interviewedRange}
                                        <Tooltip 
                                            title="Self-reported number of candidates interviewed at current and past companies" 
                                            arrow
                                            placement="top"
                                        >
                                            <InfoOutlinedIcon sx={{ fontSize: '16px', color: 'text.secondary', ml: 0.5 }} />
                                        </Tooltip>
                                    </Typography>
                                </Box>
                            )}
                            {coachedRange && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', my: 0.4 }}>
                                    <Typography component="span" sx={{ width: '40%', fontWeight: 'bold' }}>Coached:</Typography>
                                    <Typography component="span" sx={{ width: '60%', textAlign: 'left', display: 'flex', alignItems: 'center' }}>
                                        {coachedRange}
                                        <Tooltip 
                                            title="Self-reported mock interviews outside EastBadge plus sessions completed on EastBadge" 
                                            arrow
                                            placement="top"
                                        >
                                            <InfoOutlinedIcon sx={{ fontSize: '16px', color: 'text.secondary', ml: 0.5 }} />
                                        </Tooltip>
                                    </Typography>
                                </Box>
                            )}
                            {coach.supported_mock_interview_types && coach.supported_mock_interview_types.length > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', my: 0.4 }}>
                                    <Typography component="span" sx={{ width: '40%', fontWeight: 'bold' }}>Focus Areas:</Typography>
                                    <Typography component="span" sx={{ width: '60%', textAlign: 'left', whiteSpace: 'pre-line' }}>
                                    {coach.supported_mock_interview_types.join('\n')}
                                    </Typography>
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', my: 0.4, }}>
                                <Typography component="span" sx={{ width: '40%', fontWeight: 'bold' }}>Next Slot:</Typography>
                                <Typography component="span" sx={{ width: '60%', textAlign: 'left' }}>{formatDate(coach.next_available_date)}</Typography>
                            </Box>
                        </Box>
                    </Box>
                    {/* {isScrollable && !isAtBottom && (
                        <KeyboardArrowDownIcon 
                            variant="caption"
                            sx={{
                            position: 'absolute',
                            bottom: 75,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            color: 'text.secondary',
                            fontSize: '1.5rem',
                            opacity: 0.6,
                            pointerEvents: 'none',
                            zIndex: 10,
                            }}
                        />
                    )} */}
                </CardContent>

                <Box sx={{ ml: 2, mr: 2, mb: 2, mt: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: -0.75 }}>
                        <Typography variant="caption" sx={{ color: '#F5973A', fontWeight: 500 }}>
                            VERIFIED PROFILE
                        </Typography>
                        <VerifiedUserIcon sx={{ fontSize: '18px', color: '#F5973A', mr: 0.5, ml: 0.5 }} />
                    </Box>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        fullWidth 
                        onClick={() => 
                            navigate(`/coaches/${coach.unique_username}?product=${product}&role=${role}&company=${company}`, { 
                                state: { id: coach.id, product, role, company } })}
                        sx={{
                            backgroundImage: 'linear-gradient(180deg, #F5973A 0%, #FFD23E 100%)', // Set the background color to #1890ff
                            '&:hover': { backgroundImage: 'linear-gradient(180deg, #ff870f 0%, #ffd102 100%)' },
                            color: '#04003F',
                            borderRadius: '4px',
                            height: '47px', // Increase the height here
                            mt: 1, 
                            mx: 'auto', 
                            width: 'calc(100% - 0px)',
                            textTransform: 'none',
                            fontSize: '1.0rem',
                        }}
                    >
                        Book an hour for ${coach.price_per_session}
                    </Button>
                </Box>
            </Card>
        </Box>
    );
};

const CoachList = ({ coaches, product, role, company }) => {
    if (coaches.length === 0) {
        return (
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                textAlign: 'center', 
                mt: {
                    xs: 15,  // Smaller margin-top for extra-small devices (mobile)
                    sm: 20,  // Slightly larger margin-top for small devices (tablets)
                    md: 25,  // For medium devices
                    lg: 30,  // Original margin-top for large devices (desktop)
                  }
              }}
            >
              No relevant coaches found.
            </Typography>
          );
    }
    const gridColumns = {
        xs: '1fr',  // One column on extra small devices
        sm: coaches.length === 1 ? '1fr' : 'repeat(2, 1fr)',  // Two columns on small devices unless there is only one coach
        md: coaches.length >= 3 ? 'repeat(3, 1fr)' : `repeat(${coaches.length}, 1fr)`  // Three columns on medium devices and up
    };
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '80vh',
            padding: '20px',            
        }}>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: gridColumns,
                gap: { xs: 2, sm: 4, md: 6 },
                justifyContent: 'center',
                // width: '100%',
                maxWidth: '1200px', // Ensure the grid does not exceed this width
            }}>
                {coaches.map((coach) => (
                    <CoachCard key={coach.unique_username} coach={coach} product={product} role={role} company={company} />
                ))}
            </Box>
        </Box>
    );
};

export default CoachList;